import type {TemplateModelV1, QuestionSetModelV1} from '@albert-io/models';
import type {StateCreator} from 'zustand';
import {List, Map} from 'immutable';
import {addToast} from '@albert-io/atomic';

import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import {
  getFolder,
  getFolderQuestionSetsQuery
} from 'client/FolderPracticeView/folderPracticeView.queries';
import type {ContentDiscoveryStoreState} from 'client/components/ContentDiscovery/store/ContentDiscoveryStore.types';

import {invalidatePartialInterest} from 'resources/mandark.resource';
import {QUESTION_SETS_QUERY_PAGE_SIZE} from '../../ContentDiscovery.utils';

type FolderState = {
  folder: null | TemplateModelV1;
  savedQuestionSets: List<QuestionSetModelV1>;
  folderId: null | string;
  isFolderOwner: boolean;
  isLoadingFolder: boolean;
  isLoadingFolderQuestions: boolean;
  errorLoadingFolder: boolean;
};

type FolderActions = {
  setFolderId: (folderId: string) => void;
  setFolder: (folder: TemplateModelV1) => void;
  // setIsLoadingFolder: (isLoading: boolean) => void;

  /* async operations */
  fetchAndSetFolderOrError: (folderId: string) => Promise<fetchFolderReturnObj>;
  saveFolder: (questionSets: List<QuestionSetModelV1>) => Promise<boolean>;
  reset: () => void;
};

type fetchFolderReturnObj = {
  folder: FolderState['folder'];
  questionSets: FolderState['savedQuestionSets'];
  isFolderOwner: FolderState['isFolderOwner'];
  errorLoadingFolder: FolderState['errorLoadingFolder'];
  error?: any;
};

const initialState = {
  folderId: null,
  folder: null,
  savedQuestionSets: List<QuestionSetModelV1>(),
  isFolderOwner: false,
  isLoadingFolder: false,
  isLoadingFolderQuestions: false,
  errorLoadingFolder: false
};

export type FolderStoreSlice = FolderState & FolderActions;

export const createFolderStoreSlice: StateCreator<
  ContentDiscoveryStoreState,
  [['zustand/immer', never]],
  [],
  FolderStoreSlice
> = (set, get, _): FolderStoreSlice => ({
  ...initialState,
  setFolderId: (folderId) =>
    set((state) => {
      state.folderSlice.folderId = folderId;
    }),
  setFolder: (folder) =>
    set((state) => {
      state.folderSlice.folder = folder;
    }),
  fetchAndSetFolderOrError: async (folderId) => {
    set((state) => {
      state.folderSlice.isLoadingFolder = true;
    });
    const returnObj: fetchFolderReturnObj = {
      folder: null,
      questionSets: List(),
      isFolderOwner: false,
      errorLoadingFolder: false
    };

    try {
      const folderResult = await getFolder(folderId);

      // Fetch first page of question sets
      const firstQuery = getFolderQuestionSetsQuery(folderId, QUESTION_SETS_QUERY_PAGE_SIZE).page(
        1
      );
      const firstPageQuestionSets = await firstQuery.getResourcePromise();

      set((state) => {
        state.folderSlice.folder = folderResult;
        state.folderSlice.savedQuestionSets = firstPageQuestionSets;
        // Treat the masqueraded teacher as the owner so an admin editing that teacher's folder gets the
        // editor, not the read-only shared view
        state.folderSlice.isFolderOwner =
          masqueradeStore.getUserIdByMasqueradeState() === folderResult.getMeta().getOwnerId();
      });

      returnObj.folder = folderResult;
      returnObj.questionSets = firstPageQuestionSets;

      const metadata = firstQuery.getResourceMetadata();
      const totalPages = metadata.getIn(['page', 'total_pages'], 0);

      // Load remaining pages in background if there are more
      if (totalPages > 1) {
        set((state) => {
          state.folderSlice.isLoadingFolderQuestions = true;
        });
        (async () => {
          let accumulatedQuestions = firstPageQuestionSets;
          const parallelBatchSize = 5;
          let hasError = false;

          for (let startPage = 2; startPage <= totalPages; startPage += parallelBatchSize) {
            // Check if folder has changed before applying this batch
            const currentFolderId = get().folderSlice.folderId;
            if (currentFolderId !== folderId) {
              return;
            }

            try {
              const endPage = Math.min(startPage + parallelBatchSize - 1, totalPages);
              const pagePromises: any[] = [];

              for (let page = startPage; page <= endPage; page++) {
                const queryBuilder = getFolderQuestionSetsQuery(
                  folderId,
                  QUESTION_SETS_QUERY_PAGE_SIZE
                ).page(page);
                pagePromises.push(queryBuilder.getResourcePromise());
              }

              const batchResults = await Promise.all(pagePromises);

              batchResults.forEach((pageResults) => {
                accumulatedQuestions = accumulatedQuestions.concat(pageResults);
              });

              // Check again after async operation completes
              const currentFolderIdAfterFetch = get().folderSlice.folderId;
              if (currentFolderIdAfterFetch !== folderId) {
                return;
              }

              set((state) => {
                state.folderSlice.savedQuestionSets = accumulatedQuestions;
                // Also update the draft's selected question sets so the UI reflects the new questions
                state.draft.selectedQuestionSets = accumulatedQuestions;
              });
            } catch (_e) {
              hasError = true;
              break;
            }
          }

          // Final check before marking loading complete
          const currentFolderIdFinal = get().folderSlice.folderId;
          if (currentFolderIdFinal === folderId) {
            set((state) => {
              if (hasError) {
                state.folderSlice.errorLoadingFolder = true;
                state.folderSlice.isLoadingFolderQuestions = false;
              } else {
                state.folderSlice.isLoadingFolderQuestions = false;
                state.folderSlice.errorLoadingFolder = false;
              }
            });

            if (hasError) {
              addToast({
                color: 'negative',
                title: 'Error loading questions',
                message: 'Some questions failed to load. Please refresh the page to try again.'
              });
            }
          }
        })();
      }
    } catch (err) {
      set((state) => {
        state.folderSlice.errorLoadingFolder = true;
      });
      returnObj.errorLoadingFolder = true;
      returnObj.error = err;
    } finally {
      set((state) => {
        state.folderSlice.isLoadingFolder = false;
      });
    }
    return returnObj;
  },
  saveFolder: async (questionSets) => {
    const {folder, errorLoadingFolder} = get().folderSlice;
    // Saving without a fully loaded folder would replace its question sets
    // with a partial list; callers toast on the false return
    if (!folder || errorLoadingFolder) {
      console.error('saveFolder blocked: folder is missing or failed to fully load');
      return false;
    }
    set((state) => {
      state.folderSlice.isLoadingFolder = true;
    });
    let saveSuccessfull = false;
    try {
      const questionSetPositions = questionSets.reduce(
        (map, questionSet, index) => map!.set(questionSet.getId(), {position: index}),
        Map()
      );

      const updatedFolder = await folder
        .replaceRelationship({
          type: 'question_sets_v1',
          relation: questionSets,
          customMeta: questionSetPositions
        })
        .save();

      invalidatePartialInterest({resourcePath: ['templates_v1', updatedFolder.getId()]});
      invalidatePartialInterest({
        resourcePath: ['templates_v1', updatedFolder.getId(), 'question_sets_v1']
      });
      set((state) => {
        state.folderSlice.folder = updatedFolder;
        state.folderSlice.savedQuestionSets = questionSets;
      });
      saveSuccessfull = true;
    } catch (err) {
      logger.error('Error saving folder question sets: %o', err);
      // toast ?? or set saving error
    } finally {
      set((state) => {
        state.folderSlice.isLoadingFolder = false;
      });
    }
    return saveSuccessfull;
  },

  reset: () =>
    set((state) => {
      state.folderSlice = {...state.folderSlice, ...initialState};
    })
});
